import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../config/api';
import OtherToolsSection from '../OtherToolsSection';

const faqs = [
  {
    q: 'What is an MX record?',
    a: 'An MX (Mail Exchange) record specifies the mail servers responsible for receiving email on behalf of your domain.'
  },
  {
    q: 'Why are MX records important?',
    a: 'Without correct MX records, your domain cannot receive email.'
  },
  {
    q: 'How do I check my MX records?',
    a: 'Use this MX Analyser or command-line tools (dig, nslookup) to view your MX records.'
  },
  {
    q: 'Can I have multiple MX records?',
    a: 'Yes, you can have multiple MX records with different priorities for redundancy.'
  },
  {
    q: 'What does MX priority mean?',
    a: 'Lower numbers have higher priority. Mail is delivered to the lowest-numbered server first.'
  },
  {
    q: 'What happens if I have no MX records?',
    a: 'Mail servers will try to deliver to the A record. If that fails, your domain cannot receive email.'
  },
  {
    q: 'How do I fix MX record issues?',
    a: 'Update your DNS provider with the correct MX records for your mail service.'
  },
  {
    q: 'How long do MX record changes take?',
    a: 'DNS changes can take up to 48 hours to propagate, but usually update within a few hours.'
  },
  {
    q: 'Can I use a third-party mail provider?',
    a: "Yes, set your MX records to point to your provider's mail servers."
  },
  {
    q: 'What is an MX backup?',
    a: 'A backup MX server receives mail if the primary server is unavailable.'
  }
];

interface MXCheck {
  label: string;
  status: string;
  details: string | null;
}

const MXAnalyzer: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const validateDomain = (d: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(d);

  const analyzeMXRecords = (mx: any): MXCheck[] => {
    const checks: MXCheck[] = [];
    if (!mx || !Array.isArray(mx) || mx.length === 0) {
      checks.push({
        label: 'MX Records',
        status: 'Error (No MX records found)',
        details: 'No MX records were found for this domain.'
      });
      return checks;
    }
    checks.push({
      label: 'MX Records',
      status: 'Pass',
      details: `Found ${mx.length} MX record${mx.length > 1 ? 's' : ''}.`
    });
    // Check for common issues
    const priorities = mx.map((r: any) => r.pri ?? r.priority ?? null).filter((p: any) => p !== null);
    if (priorities.length !== mx.length) {
      checks.push({
        label: 'Priority',
        status: 'Warning (Missing priority)',
        details: 'One or more MX records are missing a priority value.'
      });
    } else {
      checks.push({
        label: 'Priority',
        status: 'Pass',
        details: `Priorities: ${priorities.join(', ')}`
      });
    }
    // Check for backup MX
    if (mx.length > 1) {
      checks.push({
        label: 'Backup MX',
        status: 'Pass',
        details: 'Multiple MX records found. Backup MX is configured.'
      });
    } else {
      checks.push({
        label: 'Backup MX',
        status: 'Warning (No backup MX)',
        details: 'Only one MX record found. Consider adding a backup MX for redundancy.'
      });
    }
    // Check for common mail providers
    const mxHosts = mx.map((r: any) => r.target ?? r.exchange ?? r.host ?? '').join(' ').toLowerCase();
    if (mxHosts.includes('google.com')) {
      checks.push({
        label: 'Provider',
        status: 'Pass',
        details: 'Google Workspace detected.'
      });
    } else if (mxHosts.includes('outlook.com') || mxHosts.includes('office365.com')) {
      checks.push({
        label: 'Provider',
        status: 'Pass',
        details: 'Microsoft 365 detected.'
      });
    } else if (mxHosts.includes('zoho.com')) {
      checks.push({
        label: 'Provider',
        status: 'Pass',
        details: 'Zoho Mail detected.'
      });
    } else {
      checks.push({
        label: 'Provider',
        status: 'Pass',
        details: 'Custom or other provider.'
      });
    }
    return checks;
  };

  const handleAnalyze = async () => {
    setError('');
    setResult(null);
    if (!domain) {
      setError('Please enter a domain.');
      return;
    }
    if (!validateDomain(domain)) {
      setError('Please enter a valid domain.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/dns/check/MX?domain=${encodeURIComponent(domain)}`);
      console.log('MX API Response:', response.data); // Debug log
      
      if (!response.data || !response.data.dns || !response.data.dns.mx || response.data.dns.mx.length === 0) {
        setError('No MX records found for this domain.');
        setResult(null);
      } else {
        const resultWithChecks = {
          ...response.data,
          checks: analyzeMXRecords(response.data.dns.mx),
          mxRecords: response.data.dns.mx
        };
        setResult(resultWithChecks);
      }
    } catch (err: any) {
      console.error('MX analysis error:', err);
      setError(err.response?.data?.error || 'Failed to fetch MX data. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!result || !result.mxRecords) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = pageHeight / 2 - 60;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor('#0B60B1');
    doc.text('MX AUDIT REPORT', 60, y);
    y += 40;
    doc.setFontSize(18);
    doc.setTextColor('#222');
    doc.text(`Domain: ${domain}`, 60, y);
    y += 28;
    doc.setFontSize(12);
    doc.setTextColor('#888');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}   Year: ${now.getFullYear()}`, 60, y);
    y += 22;
    doc.setFontSize(10);
    doc.setTextColor('#888');
    doc.text('Generated by: QuillBee – Deliverability Agent for ImpactQuill', 60, y);
    doc.addPage();
    // Header for page 2
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor('#0B60B1');
    doc.text(`MX report for ${domain}`, 40, 110);
    let y2 = 140;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#0B60B1');
    doc.text('MX Records:', 40, y2);
    y2 += 18;
    doc.setFont('courier', 'normal');
    doc.setFontSize(11);
    doc.setTextColor('#222');
    result.mxRecords.forEach((mx: any) => {
      doc.text(`Priority: ${mx.pri ?? mx.priority ?? ''}  Host: ${mx.target ?? mx.exchange ?? mx.host ?? ''}`, 40, y2);
      y2 += 16;
    });
    y2 += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(result.mxRecords.length > 0 ? '#388e3c' : '#d32f2f');
    doc.text(`Status: ${result.mxRecords.length > 0 ? 'PASS' : 'FAIL'}`, 40, y2);
    y2 += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor('#0B60B1');
    doc.text('MX Checks:', 40, y2);
    y2 += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor('#222');
    if (Array.isArray(result.checks)) {
      result.checks.forEach((c: any) => {
        doc.text(`• ${c.label}: ${c.status}`, 60, y2);
        y2 += 18;
      });
    }
    // Thank you page
    doc.addPage();
    doc.setFillColor(11, 96, 177); // #0B60B1
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(28);
    doc.setTextColor('#fff');
    doc.text('Thank you for choosing ImpactQuill', pageWidth / 2, pageHeight / 2, { align: 'center' });
    doc.save(`${domain}_mx_audit_report.pdf`);
  };

  const handleDownloadJPG = async () => {
    if (!resultRef.current) return;
    const canvas = await html2canvas(resultRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${domain}_mx_result.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', py: 0 }}>
      {/* Hero Banner */}
      <Box sx={{ bgcolor: '#0B60B1', color: '#fff', py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>MX Analyzer</Typography>
        <Typography variant="h6" maxWidth={600} mx="auto">
          Analyze and validate your MX records for best practices.
        </Typography>
      </Box>
      {/* Input Section */}
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" mb={2}>Enter your domain to check MX records:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', maxWidth: 400 }}>
          <TextField
            label="Domain"
            variant="outlined"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain) handleAnalyze();
            }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAnalyze} 
            disabled={loading || !domain} 
            sx={{ 
              minWidth: 120,
              height: 56, // Match TextField height
              '& .MuiCircularProgress-root': {
                color: 'white'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </Box>
        {loading && <CircularProgress sx={{ mt: 3 }} />}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Box>
      {/* Result Section */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {result && result.mxRecords && (
        <Card ref={resultRef} sx={{ width: '100%', maxWidth: 900, mx: 'auto', mb: 4, p: 2, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <MuiLink href={`https://${domain}`} target="_blank" rel="noopener noreferrer" underline="hover" variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {domain}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy MX Records'}>
                  <IconButton onClick={() => { navigator.clipboard.writeText(result.mxRecords.map((mx: any) => `${mx.pri ?? mx.priority ?? ''} ${mx.target ?? mx.exchange ?? mx.host ?? ''}`).join('\n')); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF}>
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as JPG">
                  <IconButton onClick={handleDownloadJPG}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={result.mxRecords.length > 0 ? 'PASS' : 'FAIL'}
                color={result.mxRecords.length > 0 ? 'success' : 'error'}
                sx={{ 
                  fontWeight: 700, 
                  fontSize: 16, 
                  px: 2, 
                  py: 1, 
                  mb: 1,
                  '& .MuiChip-icon': {
                    color: 'inherit'
                  }
                }}
              />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>MX Records</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f7fafd', mb: 2 }}>
              <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 16 }}>
                {result.mxRecords.map((mx: any) => `${mx.pri ?? mx.priority ?? ''} ${mx.target ?? mx.exchange ?? mx.host ?? ''}`).join('\n')}
              </Typography>
            </Paper>
            <Divider sx={{ my: 2 }} />
            {/* MX Analysis Table */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>MX Analysis</Typography>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, bgcolor: '#f7fafd', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                <tbody>
                  {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check: MXCheck, i: number) => (
                    <tr key={i}>
                      <td style={{ verticalAlign: 'top', padding: '12px 8px 12px 16px', width: 40, borderBottom: '1px solid #e0e0e0' }}>
                        {check.status.includes('Pass') ? (
                          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22, mt: 0.5 }} />
                        ) : check.status.includes('Warning') ? (
                          <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 22, mt: 0.5 }} />
                        ) : (
                          <ErrorIcon sx={{ color: '#d32f2f', fontSize: 22, mt: 0.5 }} />
                        )}
                      </td>
                      <td style={{ verticalAlign: 'top', padding: '12px 8px', fontWeight: 700, width: '180px', borderBottom: '1px solid #e0e0e0' }}>
                        {check.label}
                      </td>
                      <td style={{ verticalAlign: 'top', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                        {check.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            {/* MX Card Grid Breakdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Tag Breakdown</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check: MXCheck, i: number) => (
                  <Paper key={i} elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
                    {check.status.includes('Pass') ? (
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28, mt: 0.5 }} />
                    ) : check.status.includes('Warning') ? (
                      <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 28, mt: 0.5 }} />
                    ) : (
                      <ErrorIcon sx={{ color: '#d32f2f', fontSize: 28, mt: 0.5 }} />
                    )}
                    <Box>
                      <Typography fontWeight={700}>{check.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{check.details}</Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>MX FAQ</Typography>
          {faqs.map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>
      <OtherToolsSection current="MX Analyzer" />
    </Box>
  );
};

export default MXAnalyzer; 